const truncateDisplayName = (rawName, maxLength = 14) => {
  const normalizedName = typeof rawName === 'string' ? rawName.trim() : '';
  if (!normalizedName) return '';
  if (normalizedName.length <= maxLength + 1) return normalizedName;
  return `${normalizedName.slice(0, maxLength)}...`;
};

const showSimpleTapTooltip = (anchorElement, tooltipText) => {
  if (!anchorElement || !tooltipText) return;

  document.querySelectorAll('.simple-tap-tooltip').forEach((el) => el.remove());

  const tooltip = document.createElement('div');
  tooltip.className = 'simple-tap-tooltip';
  tooltip.textContent = tooltipText;
  tooltip.style.position = 'fixed';
  tooltip.style.zIndex = '10001';
  tooltip.style.padding = '8px 10px';
  tooltip.style.maxWidth = '260px';
  tooltip.style.borderRadius = '8px';
  tooltip.style.background = 'rgba(0, 0, 0, 0.92)';
  tooltip.style.color = 'var(--text-color)';
  tooltip.style.fontSize = '0.85rem';
  tooltip.style.lineHeight = '1.3';
  tooltip.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.45)';
  tooltip.style.border = '1px solid rgba(255, 255, 255, 0.15)';
  tooltip.style.pointerEvents = 'none';
  tooltip.style.opacity = '0';
  tooltip.style.transition = 'opacity 0.14s ease-in-out';
  document.body.appendChild(tooltip);

  const anchorRect = anchorElement.getBoundingClientRect();
  const tooltipRect = tooltip.getBoundingClientRect();
  const left = Math.max(8, Math.min(anchorRect.left, window.innerWidth - tooltipRect.width - 8));
  const topCandidate = anchorRect.top - tooltipRect.height - 8;
  const top = topCandidate > 8 ? topCandidate : Math.min(window.innerHeight - tooltipRect.height - 8, anchorRect.bottom + 8);
  tooltip.style.left = `${left}px`;
  tooltip.style.top = `${top}px`;

  requestAnimationFrame(() => {
    tooltip.style.opacity = '1';
  });

  const closeTooltip = () => {
    tooltip.style.opacity = '0';
    setTimeout(() => {
      tooltip.remove();
    }, 150);
    document.removeEventListener('touchstart', handleOutsideTouch, true);
    document.removeEventListener('click', handleOutsideTouch, true);
  };

  const handleOutsideTouch = (event) => {
    if (event.target === anchorElement) return;
    closeTooltip();
  };

  setTimeout(() => {
    document.addEventListener('touchstart', handleOutsideTouch, true);
    document.addEventListener('click', handleOutsideTouch, true);
  }, 0);
  setTimeout(closeTooltip, 1500);
};

const PLACEHOLDER_SPOT_IMAGE = '/assets/images/placeholder.svg';

const isMissingBannerUrl = (imageUrl) => {
  if (typeof imageUrl !== 'string') return true;
  const trimmed = imageUrl.trim();
  return !trimmed || trimmed === PLACEHOLDER_SPOT_IMAGE;
};

const applySpotImageFallback = (spotImage, { tryAltCdnHost = false } = {}) => {
  spotImage.onerror = () => {
    if (tryAltCdnHost && !spotImage.dataset.triedAltCdnHost && spotImage.src.startsWith('https://cdn.kissandra.ai/')) {
      spotImage.dataset.triedAltCdnHost = 'true';
      spotImage.src = spotImage.src.replace('https://cdn.kissandra.ai', 'https://kissandra.ai/cdn');
      return;
    }
    if (spotImage.src.endsWith('.webp') && !spotImage.dataset.triedPng) {
      spotImage.dataset.triedPng = 'true';
      spotImage.src = spotImage.src.replace(/\.webp$/, '.png');
      return;
    }
    spotImage.onerror = null;
    spotImage.closest('.spot-button')?.remove();
  };
};

export const getScenarioCharCount = (spot) => {
  if (Number.isFinite(spot?.charCount) && spot.charCount > 0) return spot.charCount;
  if (Array.isArray(spot?.chars)) return spot.chars.length;
  return 0;
};

export const buildSpotButton = (spot, { type = 'public', onSpotClick, onDescriptionClick, membershipLocked = false } = {}) => {
  if (isMissingBannerUrl(spot?.imageUrl)) return null;
  const spotId = spot?.id ?? spot?.scenario_id;
  const description = spot.description || '';
  const spotButton = document.createElement('button');
  spotButton.className = 'spot-button';
  spotButton.setAttribute('data-spot-id', spotId);
  spotButton.setAttribute('data-spot-type', type);
  spotButton.setAttribute('aria-label', spot.name);
  if (membershipLocked) {
    spotButton.classList.add('is-membership-locked');
    spotButton.setAttribute('aria-disabled', 'true');
  }

  const spotContainer = document.createElement('div');
  spotContainer.className = 'spot-container';

  const spotImage = document.createElement('img');
  spotImage.className = 'spot-image';
  spotImage.alt = spot.name;
  spotImage.src = spot.imageUrl;
  applySpotImageFallback(spotImage, { tryAltCdnHost: type === 'private' });

  const titleElement = document.createElement('h4');
  titleElement.className = 'spot-title';
  titleElement.textContent = spot.name;

  if (description) {
    const expandIcon = document.createElement('button');
    expandIcon.type = 'button';
    expandIcon.className = 'description-expand-icon';
    expandIcon.innerHTML = '&#8505;';
    expandIcon.tabIndex = 0;
    expandIcon.setAttribute('aria-label', `${spot.name} details`);
    expandIcon.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      onDescriptionClick?.(spot.name, description, e.currentTarget);
    });
    expandIcon.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      e.stopPropagation();
      onDescriptionClick?.(spot.name, description, e.currentTarget);
    });
    spotContainer.appendChild(expandIcon);
  }

  spotContainer.appendChild(spotImage);
  spotContainer.appendChild(titleElement);
  spotButton.appendChild(spotContainer);

  if (onSpotClick) spotButton.addEventListener('click', onSpotClick);
  return spotButton;
};

export const buildScenarioButton = (spot, handlers) => buildSpotButton(spot, { ...handlers, type: 'public' });
export const buildPrivateScenarioButton = (scenario, handlers) => buildSpotButton(scenario, { ...handlers, type: 'private' });

export const buildConversationButton = (conversation, {
  backgroundImage,
  onSpotClick,
  onDescriptionClick,
  formatRelativeTime
}) => {
  const fullScenarioName = (conversation?.scenarioName || 'Unknown Scenario').trim() || 'Unknown Scenario';
  const truncatedScenarioName = truncateDisplayName(fullScenarioName, 14);
  const conversationButton = document.createElement('button');
  conversationButton.className = 'spot-button conversation-button';
  conversationButton.setAttribute('data-conversation-id', conversation.conversationId);
  conversationButton.setAttribute('data-character-id', conversation.characterId);
  conversationButton.setAttribute('data-spot-id', conversation.scenarioId);
  conversationButton.setAttribute('data-spot-type', 'premium');
  conversationButton.setAttribute('data-full-name', fullScenarioName);
  conversationButton.setAttribute('data-conversation-data', JSON.stringify(conversation));
  conversationButton.setAttribute('aria-label', `Continue conversation: ${conversation.displayName || 'Unknown'}`);

  conversationButton.style.background = `url('${backgroundImage}') center/cover no-repeat`;

  const overlay = document.createElement('div');
  overlay.className = 'conversation-overlay';

  const conversationPreview = document.createElement('div');
  conversationPreview.className = 'conversation-preview';

  const lastMessageText = conversation.lastMessage || '';

  if (lastMessageText.length > 0) {
    const expandIcon = document.createElement('button');
    expandIcon.type = 'button';
    expandIcon.className = 'description-expand-icon';
    expandIcon.innerHTML = '&#8505;';
    expandIcon.tabIndex = 0;
    expandIcon.setAttribute('aria-label', 'Last message details');
    expandIcon.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      onDescriptionClick?.('Last Message', lastMessageText, e.currentTarget);
    });
    conversationButton.appendChild(expandIcon);
  }

  const metaInfo = document.createElement('div');
  metaInfo.className = 'conversation-meta';

  const timestamp = document.createElement('span');
  timestamp.className = 'conversation-timestamp';
  const date = new Date(conversation.lastUpdated || conversation.createdAt);
  timestamp.textContent = formatRelativeTime?.(date) || '';

  const messageCount = document.createElement('span');
  messageCount.className = 'conversation-message-count';
  const totalMessages = conversation.messageCount || 0;
  messageCount.textContent = `${totalMessages} msgs`;

  metaInfo.appendChild(messageCount);
  metaInfo.appendChild(timestamp);
  conversationPreview.appendChild(metaInfo);

  conversationButton.appendChild(overlay);
  conversationButton.appendChild(conversationPreview);

  const scenarioTitle = document.createElement('h4');
  scenarioTitle.className = 'spot-title conversation-scenario-title';
  scenarioTitle.textContent = truncatedScenarioName;
  scenarioTitle.setAttribute('data-full-name', fullScenarioName);
  scenarioTitle.setAttribute('title', fullScenarioName);

  let didHandleTouchTitle = false;
  const openScenarioNameTooltip = (event) => {
    event.preventDefault();
    event.stopPropagation();
    showSimpleTapTooltip(scenarioTitle, fullScenarioName);
  };

  scenarioTitle.addEventListener('touchend', (event) => {
    didHandleTouchTitle = true;
    openScenarioNameTooltip(event);
  }, { passive: false });
  scenarioTitle.addEventListener('click', (event) => {
    if (didHandleTouchTitle) {
      didHandleTouchTitle = false;
      return;
    }

    const isTouchDevice = window.matchMedia?.('(hover: none), (pointer: coarse)')?.matches;
    if (!isTouchDevice) return;
    openScenarioNameTooltip(event);
  });
  conversationButton.appendChild(scenarioTitle);

  if (onSpotClick) {
    conversationButton.addEventListener('click', onSpotClick);
  }

  return conversationButton;
};

export const buildCreateScenarioCard = () => {
  const createCardButton = document.createElement('button');
  createCardButton.type = 'button';
  createCardButton.className = 'spot-button create-scenario-card';
  createCardButton.setAttribute('data-spot-type', 'create');
  createCardButton.setAttribute('data-spot-id', 'create-scenario');
  createCardButton.setAttribute('aria-label', 'Create a scenario');
  createCardButton.addEventListener('click', () => {
    window.location.href = './create-scenario.html';
  });

  const spotContainer = document.createElement('div');
  spotContainer.className = 'spot-container';

  const plus = document.createElement('div');
  plus.className = 'create-scenario-plus';
  plus.textContent = '+';

  const titleElement = document.createElement('h4');
  titleElement.className = 'spot-title';
  titleElement.textContent = 'Create Scenario';
  titleElement.setAttribute('data-translate', 'rankings.createScenario');

  spotContainer.appendChild(plus);
  spotContainer.appendChild(titleElement);
  createCardButton.appendChild(spotContainer);

  return createCardButton;
};

export const buildSpotsHeader = ({ onSearchClick, onShuffleClick }) => {
  const headerContainer = document.createElement('div');
  headerContainer.className = 'spots-header-container';

  const gearButton = document.createElement('button');
  gearButton.className = 'gear-button search-button';
  gearButton.innerHTML = '🔍';
  gearButton.setAttribute('title', '');
  gearButton.setAttribute('data-translate-attr', 'title:rankings.search');

  gearButton.addEventListener('click', (e) => {
    e.stopPropagation();
    onSearchClick?.();
  });

  const shuffleButton = document.createElement('button');
  shuffleButton.className = 'gear-button shuffle-button';
  shuffleButton.innerHTML = '❓';
  shuffleButton.setAttribute('title', '');
  shuffleButton.setAttribute('data-translate-attr', 'title:rankings.shuffleScenarios');
  shuffleButton.addEventListener('click', (e) => {
    e.stopPropagation();
    onShuffleClick?.();
  });

  const heading = document.createElement('h3');
  heading.className = 'section-title spots-title';
  heading.setAttribute('data-translate', 'rankings.scenarios');
  heading.textContent = 'Scenarios';

  const leftGroup = document.createElement('div');
  leftGroup.className = 'header-controls-left';
  leftGroup.appendChild(heading);

  const rightGroup = document.createElement('div');
  rightGroup.className = 'header-controls-right';
  rightGroup.appendChild(gearButton);
  rightGroup.appendChild(shuffleButton);

  headerContainer.appendChild(leftGroup);
  headerContainer.appendChild(rightGroup);

  return headerContainer;
};

export const buildViewTabs = (activeView, onTabClick) => {
  const viewTabs = document.createElement('div');
  viewTabs.className = 'spots-view-tabs';
  viewTabs.setAttribute('role', 'tablist');
  viewTabs.setAttribute('aria-label', 'Scenario view');

  const tabDefs = [
    { view: 'scenarios', label: 'Public', translateKey: 'rankings.public' },
    { view: 'private', label: 'Private', translateKey: 'rankings.privateScenarios' },
    { view: 'conversations', label: 'Recent', translateKey: 'rankings.recentConversations' }
  ];

  const handleTabKeyDown = (e) => {
    const key = e.key;
    if (key !== 'ArrowLeft' && key !== 'ArrowRight') return;
    e.preventDefault();
    /** @type {HTMLButtonElement[]} */
    const tabs = Array.from(
      /** @type {NodeListOf<HTMLButtonElement>} */ (viewTabs.querySelectorAll('button.spots-view-tab'))
    );
    const currentIndex = tabs.findIndex(t => t === e.currentTarget);
    if (currentIndex === -1) return;
    const nextIndex = key === 'ArrowRight'
      ? (currentIndex + 1) % tabs.length
      : (currentIndex - 1 + tabs.length) % tabs.length;
    tabs[nextIndex]?.focus();
  };

  tabDefs.forEach(({ view, label, translateKey }) => {
    const tabButton = document.createElement('button');
    tabButton.type = 'button';
    tabButton.className = 'spots-view-tab';
    tabButton.dataset.view = view;
    tabButton.setAttribute('role', 'tab');
    tabButton.setAttribute('data-translate', translateKey);
    tabButton.textContent = label;

    const isActive = view === activeView;
    if (isActive) tabButton.classList.add('active');
    tabButton.setAttribute('aria-selected', isActive ? 'true' : 'false');
    tabButton.setAttribute('tabindex', isActive ? '0' : '-1');

    tabButton.addEventListener('click', (e) => {
      e.stopPropagation();
      onTabClick?.(view);
    });
    tabButton.addEventListener('keydown', handleTabKeyDown);

    viewTabs.appendChild(tabButton);
  });

  return viewTabs;
};

const buildSearchBar = (searchId) => {
  const searchBar = document.createElement('input');
  searchBar.type = 'text';
  searchBar.className = 'spot-search-bar';
  searchBar.id = searchId;
  searchBar.setAttribute('name', searchId);
  searchBar.setAttribute('placeholder', 'Filter by name...');
  searchBar.setAttribute('data-translate-attr', 'placeholder:rankings.filterByName');
  return searchBar;
};

export const buildCharCountFilter = ({ selected = 'any', onChange } = {}) => {
  const row = document.createElement('div');
  row.className = 'spot-char-count-filter';
  row.setAttribute('role', 'group');
  row.setAttribute('aria-label', 'Filter by number of characters');

  const label = document.createElement('span');
  label.className = 'spot-char-count-label';
  label.textContent = 'Chars';
  label.setAttribute('data-translate', 'rankings.charCountFilter');
  row.appendChild(label);

  /** @type {Array<'1'|'2'|'3'|'any'>} */
  const values = ['1', '2', '3', 'any'];
  values.forEach((value) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'spot-char-count-btn';
    btn.dataset.charCount = value;
    btn.textContent = value === 'any' ? 'Any' : value;
    btn.setAttribute('aria-pressed', String(value === selected));
    btn.setAttribute('tabindex', '0');
    if (value === selected) btn.classList.add('is-selected');
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      onChange?.(value);
    });
    row.appendChild(btn);
  });

  return row;
};

export const syncCharCountFilter = (container, selected) => {
  if (!container) return;
  container.querySelectorAll('.spot-char-count-btn').forEach((btn) => {
    const value = btn.dataset.charCount;
    const isSelected = value === selected;
    btn.classList.toggle('is-selected', isSelected);
    btn.setAttribute('aria-pressed', String(isSelected));
  });
};

export const buildSpotFiltersPanel = ({ panelId, searchId, tagsPicker, charCountFilter }) => {
  const panel = document.createElement('div');
  panel.className = 'spot-filters-panel';
  panel.id = panelId;

  panel.appendChild(buildSearchBar(searchId));
  if (charCountFilter) panel.appendChild(charCountFilter);
  if (tagsPicker) panel.appendChild(tagsPicker);

  return panel;
};

export const buildSpotRow = (rowId) => {
  const spotRow = document.createElement('div');
  spotRow.className = 'spot-row';
  spotRow.id = rowId;
  return spotRow;
};

const SPOTS_VIEW_DOM = {
  scenarios: {
    searchId: 'public-spot-search',
    panelId: 'public-spot-filters',
    firstSpotSelector: '.spot-button[data-spot-type="public"]'
  },
  conversations: {
    searchId: 'premium-spot-search',
    panelId: 'premium-spot-filters',
    firstSpotSelector: '.spot-button[data-spot-type="premium"]'
  },
  private: {
    searchId: 'private-spot-search',
    panelId: 'private-spot-filters',
    firstSpotSelector: '.spot-button[data-spot-type="private"]'
  }
};

export const getSpotsViewDom = (view) => SPOTS_VIEW_DOM[view] || SPOTS_VIEW_DOM.scenarios;

export const spotMatchesFilters = (spot, { searchTerm, descriptionText, tagIds, charCountFilter }, resolveTagIds) => {
  if (searchTerm) {
    const titleMatch = (spot.name || '').toLowerCase().includes(searchTerm);
    const descriptionMatch = (descriptionText || spot.description || '').toLowerCase().includes(searchTerm);
    if (!titleMatch && !descriptionMatch) return false;
  }

  if (tagIds?.length > 0) {
    const spotTagIds = resolveTagIds(spot.tags || []);
    if (!tagIds.every((id) => spotTagIds.includes(id))) return false;
  }

  if (charCountFilter && charCountFilter !== 'any') {
    const expected = Number(charCountFilter);
    const actual = Number.isFinite(spot.charCount)
      ? spot.charCount
      : (Array.isArray(spot.chars) ? spot.chars.length : 0);
    if (actual !== expected) return false;
  }

  return true;
};

export const suggestCharCountFromParty = (partySize) => {
  if (partySize === 1 || partySize === 2 || partySize === 3) return String(partySize);
  return 'any';
};
